// Icons shared across views, so the same idea always looks the same. The trophy
// marks a win in the account menu ("Moje výhry") and on a solved calendar day —
// keeping one copy stops the two from drifting apart.
import React from "react";

const base = (size, className) => ({
  viewBox: "0 0 24 24",
  width: size,
  height: size,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  className,
});

// „Otevře se" na konci klikatelného řádku. Kreslené, ne znak `›`: textový glyf
// se sází na účtaří, takže jeho inkoust neleží uprostřed svého řádku a proti
// jménu vedle sebe vypadá posunutý — a o kolik, závisí na fontu. SVG má střed
// tam, kde ho nakreslíme.
export const ChevronRight = ({ size = 18, className }) => (
  <svg {...base(size, className)}>
    <path d="M9.5 5.5l6.5 6.5-6.5 6.5" />
  </svg>
);

// A day started but not solved — same glyph as the menu's "Rozehrané hry".
export const PlayIcon = ({ size = 16, className }) => (
  <svg {...base(size, className)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M10 8.5l6 3.5-6 3.5z" />
  </svg>
);

// A day still waiting to be played — a bare question mark (no ring), drawn large
// enough to carry the cell on its own.
export const QuestionIcon = ({ size = 16, className }) => (
  <svg {...base(size, className)}>
    <path d="M8 8.2a4 4 0 1 1 5.7 3.7c-1.1.6-1.7 1.6-1.7 2.8v.6" />
    <path d="M12 19.6v.02" />
  </svg>
);

// A day still waiting to be played — a treasure chest to open (clearer than a
// question mark, which players read as an error). Matches the wins-timeline chest.
export const ChestIcon = ({ size = 18, className }) => (
  <svg {...base(size, className)} strokeWidth={2}>
    <path d="M4 11h16v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8Z" fill="currentColor" fillOpacity="0.18" />
    <path d="M4 11V9a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v2" />
    <path d="M4 11h16" />
    <path d="M11 11v2.5h2V11" />
    <rect x="10.3" y="13" width="3.4" height="2.6" rx="0.5" fill="currentColor" fillOpacity="0.35" />
  </svg>
);

export const TrophyIcon = ({ size = 16, className }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M8 21h8M12 17v4" />
    <path d="M7 4h10v5a5 5 0 0 1-10 0V4z" />
    <path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />
  </svg>
);

// A filled trophy for the wins shelf. The outline TrophyIcon reads as a status
// glyph next to a label; a medal wants weight, so this one is solid and takes a
// colour from its tier class.
export const TrophyFilled = ({ size = 22, className }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M7 3h10v6a5 5 0 0 1-10 0V3z" />
    <path d="M11 14h2v4h-2z" />
    <path d="M8 20h8a1 1 0 0 1 1 1H7a1 1 0 0 1 1-1z" />
    <path d="M17 4h3v2.5a3.5 3.5 0 0 1-3.5 3.5H16V8h.5A1.5 1.5 0 0 0 18 6.5V6h-1V4zM7 4H4v2.5A3.5 3.5 0 0 0 7.5 10H8V8h-.5A1.5 1.5 0 0 1 6 6.5V6h1V4z" />
  </svg>
);

// A crown marking a Premium account — next to the name and on the account icon.
export const CrownIcon = ({ size = 14, className }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M3 8l4 3 5-6 5 6 4-3-2 11H5L3 8z" />
    <rect x="5" y="19" width="14" height="2" rx="0.5" />
  </svg>
);

// A padlock used to mark Premium-gated controls (the mega toggle for free users).
export const LockIcon = ({ size = 14, className }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

// A cloud crossed out — an archive day when the game was down.
export const CloudOffIcon = ({ size = 18, className }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M7 18h10a4 4 0 0 0 .6-7.95A6 6 0 0 0 6.2 9.1 4.5 4.5 0 0 0 7 18z" />
    <path d="M3 3l18 18" />
  </svg>
);

// ── Community ──────────────────────────────────────────────────────────────
// Both are the platforms' own marks, so they are drawn as solid shapes in
// currentColor rather than the stroked style the rest of this file uses — a
// stroked Facebook "f" or Discord face is not either brand's logo any more.

export const FacebookIcon = ({ size = 20, className }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true" className={className}>
    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.5-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94z" />
  </svg>
);

export const DiscordIcon = ({ size = 20, className }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true" className={className}>
    <path d="M19.54 5.34A16.1 16.1 0 0 0 15.5 4.1a.06.06 0 0 0-.06.03c-.17.31-.37.71-.5 1.03a14.9 14.9 0 0 0-4.48 0c-.14-.33-.34-.72-.51-1.03a.06.06 0 0 0-.06-.03c-1.41.24-2.76.66-4.03 1.24a.05.05 0 0 0-.03.02C2.28 9.15 1.65 12.85 1.96 16.5a.07.07 0 0 0 .03.05 16.2 16.2 0 0 0 4.88 2.47.06.06 0 0 0 .07-.02c.38-.51.71-1.05.99-1.61a.06.06 0 0 0-.03-.09c-.53-.2-1.03-.44-1.51-.72a.06.06 0 0 1 0-.1l.3-.24a.06.06 0 0 1 .06 0c3.17 1.45 6.6 1.45 9.73 0a.06.06 0 0 1 .06 0l.3.24a.06.06 0 0 1 0 .1c-.48.28-.98.52-1.51.72a.06.06 0 0 0-.03.09c.29.56.62 1.1.99 1.61a.06.06 0 0 0 .07.02 16.15 16.15 0 0 0 4.89-2.47.06.06 0 0 0 .03-.05c.37-4.22-.62-7.89-2.63-11.14a.05.05 0 0 0-.03-.02zM8.52 14.28c-.97 0-1.77-.89-1.77-1.98s.78-1.98 1.77-1.98c1 0 1.79.9 1.77 1.98 0 1.09-.78 1.98-1.77 1.98zm6.97 0c-.97 0-1.77-.89-1.77-1.98s.78-1.98 1.77-1.98c1 0 1.79.9 1.77 1.98 0 1.09-.77 1.98-1.77 1.98z" />
  </svg>
);
